import { _ReactPixi, AnimatedSprite, Container } from '@pixi/react';
import { useEffect, useMemo } from 'react';
import { ISpriteDisplayProps } from '../../../types/types';
import { ErrorBoundary } from '../../ErrorBoundary';
import Placeholder from '../Placeholder';
import { useSpritesStore } from '../../../store/spritesSlice';
import { getSpriteDirectionName } from '../../../utils/general.utils';
import { getAnimationSpeed } from '../../../utils/timing.utils';
import { useGameStore } from '../../../store';
import { useWorldFrozen } from '../../../hooks/useWorldFrozen';
import { tileHeight } from '../../../data/tileset.config';
import { getZIndex } from '../../../utils/zindex.utils';
import { missingAssetService } from '../../../services/MissingAssetService';
import { bundleLoaderService } from '../../../services/BundleLoaderService';

const ZERO_POS = { x: 0, y: 0 };

export default ({
  clip,
  position,
  zIndex = 10,
  entityAlpha = 1,
  ...rest
}: ISpriteDisplayProps) => {
  const textures = useSpritesStore((s) => s.textures);
  const frozen = useWorldFrozen();
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const hasParallax = (clip.parallaxDepth ?? 1) !== 1;
  const cameraPos = useGameStore((s) =>
    hasParallax ? s.cameraPosition : ZERO_POS,
  );

  const computedPosition = useMemo(() => {
    let x = position.x * zoomFactor;
    let y = -position.y * zoomFactor;
    if (hasParallax) {
      const relX = (position.x - cameraPos.x) * zoomFactor;
      const relY = (position.y - cameraPos.y) * zoomFactor;
      const depth = (clip.parallaxDepth ?? 1) - 1;
      let driftX = relX * depth;
      let driftY = relY * depth;
      if (clip.maxParallaxOffset !== undefined) {
        driftX = Math.max(
          -clip.maxParallaxOffset,
          Math.min(clip.maxParallaxOffset, driftX),
        );
        driftY = Math.max(
          -clip.maxParallaxOffset,
          Math.min(clip.maxParallaxOffset, driftY),
        );
      }
      x += driftX;
      y -= driftY;
    }
    return [x, y] as [number, number];
  }, [
    position.x,
    position.y,
    zoomFactor,
    hasParallax,
    cameraPos.x,
    cameraPos.y,
    clip.parallaxDepth,
    clip.maxParallaxOffset,
  ]);

  const spriteTexture = useMemo(
    () => textures[clip.spriteName],
    [clip.spriteName, textures],
  );

  // Safety net: a sheet not in the active bundles (e.g. a monster spawned via
  // the devtool outside its biome) self-heals by loading its owning bundle.
  useEffect(() => {
    if (!spriteTexture) {
      void bundleLoaderService.ensureSheetLoaded(clip.spriteName);
    }
  }, [spriteTexture, clip.spriteName]);

  const computedSize = useMemo(
    () => ({
      width: clip.width ? clip.width * zoomFactor : undefined,
      height: clip.height ? clip.height * zoomFactor : undefined,
      flipX: clip.direction !== undefined && [1, 2, 3].includes(clip.direction),
    }),
    [clip.width, clip.height, clip.direction, zoomFactor],
  );

  const spriteOffset = useMemo(
    () => [clip.offsetX ?? 0, clip.offsetY ?? 0] as [number, number],
    [clip.offsetX, clip.offsetY],
  );

  const scaleX = useMemo(
    () => (computedSize.flipX ? -1 : 1),
    [computedSize.flipX],
  );

  const anchor = useMemo(
    () => (clip.anchor ?? [0.5, 0.5]) as [number, number],
    [clip.anchor],
  );

  const renderedContent = useMemo(() => {
    const dirSuffix =
      clip.direction === undefined
        ? ''
        : '-' + getSpriteDirectionName(clip.direction);
    // Robust lookup: if the exact key (with or without directional suffix) does
    // not match, try the other form. Covers:
    //   - a non-directional VFX mistakenly rendered with a direction
    //   - a directional entity whose direction is temporarily undefined
    const primaryKey = `${clip.spriteName}${dirSuffix}`;
    const fallbackKey = dirSuffix
      ? clip.spriteName
      : `${clip.spriteName}-bottom`;
    const frames =
      spriteTexture?.animations[primaryKey] ??
      spriteTexture?.animations[fallbackKey];
    const sheetKey = spriteTexture?.animations[primaryKey]
      ? primaryKey
      : spriteTexture?.animations[fallbackKey]
        ? fallbackKey
        : primaryKey;
    const frameCount = frames?.length ?? 0;

    if (!frames || frames.length === 0) {
      missingAssetService.report(sheetKey, 'entity');
    }

    let animSpeed = 0.1;
    if (clip.animationSpeed !== undefined && clip.animationSpeed !== 'auto') {
      animSpeed = clip.animationSpeed;
    } else if (clip.phaseDuration && frameCount > 0) {
      animSpeed = getAnimationSpeed(frameCount, clip.phaseDuration);
    }

    return (
      <ErrorBoundary
        resetKeys={[clip.spriteName]}
        onError={() => missingAssetService.report(sheetKey, 'entity')}
        fallback={
          <Placeholder
            x={computedPosition[0]}
            y={computedPosition[1]}
            zIndex={zIndex}
            anchor={[0.5, 0.5]}
            width={computedSize?.width}
            height={computedSize?.height}
          />
        }
      >
        {!frames || frames.length === 0 ? (
          <Placeholder
            x={computedPosition[0]}
            y={computedPosition[1]}
            zIndex={zIndex}
            anchor={[0.5, 0.5]}
            width={computedSize?.width}
            height={computedSize?.height}
          />
        ) : (
          <Container
            position={computedPosition}
            scale={{ x: scaleX, y: 1 }}
            rotation={clip.rotation ?? 0}
            alpha={entityAlpha}
            zIndex={getZIndex({
              type: 'entity',
              posY: position.y / tileHeight,
            })}
          >
            <AnimatedSprite
              textures={frames}
              initialFrame={0}
              animationSpeed={animSpeed}
              loop={clip.loop ?? true}
              anchor={anchor}
              position={spriteOffset}
              zIndex={zIndex}
              key={sheetKey}
              width={computedSize?.width ?? undefined}
              height={computedSize?.height ?? undefined}
              {...(rest as _ReactPixi.IAnimatedSprite)}
              isPlaying={!frozen}
            />
          </Container>
        )}
      </ErrorBoundary>
    );
  }, [
    clip.direction,
    clip.spriteName,
    clip.animationSpeed,
    clip.phaseDuration,
    clip.loop,
    clip.rotation,
    spriteTexture?.animations,
    computedPosition,
    zIndex,
    computedSize?.width,
    computedSize?.height,
    scaleX,
    entityAlpha,
    position.y,
    anchor,
    spriteOffset,
    rest,
    frozen,
  ]);

  return <>{renderedContent}</>;
};
